import { MyGarageViewModel } from "./Types/MyGarageViewModel";
import { useViewModelInstance, reloadMyGarageViewModel } from "./Types/MyGarageViewModelStore";
import { useDialogs } from "./Types/ConfirmDialogs";
import { useMyGarageApi } from "./Types/Api/MyGarageApi";
import { useVehicleApi } from "./Types/Api/VehicleApi";
import { useConditionApi } from "./Types/Api/ConditionApi";
import { MyGarageRouteGuard } from "./Types/MyGarageRouteGuard";
import type { IConciergeItem, IConciergeCategory } from "./Types/IConciergeCategory";
import type { IMyGarageViewModel, IMyGarageViewModelDto } from "./Types/IMyGarageViewModel";

import EarlyAccess from "../Views/EarlyAccess/EarlyAccess.vue";
import MyGarage from "./Views/MyGarage.vue";
import Settings from "./Views/Settings/Settings.vue";
import HealthMonitorNotConnected from "./Views/HealthMonitor/HealthMonitorNotConnected.vue";
import CarbucksProtectFlow from "./Views/CarePlan/CarbucksProtectFlow.vue";
import SetOdometer from "./Views/SetOdometer/SetOdometer.vue";
import ServiceLog from "./Views/DigitalServiceLog/DigitalServiceLog.vue";
import CarePlan from "./Views/CarePlan/CarePlan.vue";
import History from "./Views/History/History.vue";
import VehicleDetails from "./Views/VehicleDetails/VehicleDetails.vue";
import Concierge from "./Views/Concierge/Concierge.vue";
import ConditionSettings from "./Views/ConditionSettings/ConditionSettings.vue";
 
export
{
    IMyGarageViewModel,
    IMyGarageViewModelDto,
    MyGarageViewModel,
    useMyGarageApi,
    useVehicleApi,
    useConditionApi,
    useDialogs,
    useViewModelInstance,
    reloadMyGarageViewModel,
    IConciergeItem,
    IConciergeCategory
};

export default [
    {
        path: '/my-garage',
        name: 'My Garage',
        meta: {
            allowAnonymous: false,
            title: 'My Garage'
        },
        beforeEnter: MyGarageRouteGuard,
        component: MyGarage
    },
    {
        path: '/',
        name: 'Home',
        redirect: '/my-garage',
    },
    {
        path: '/my-garage/connect-health-monitor',
        name: 'Connect Health Monitor',
        meta: {
            allowAnonymous: false,
            title: 'Connect Health Monitor',
            animation: 'scale'
        },
        component: HealthMonitorNotConnected
    },
    {
        path: '/my-garage/acknowledge-carbucks-protect',
        name: 'Acknowledge Carbucks Protect',
        meta: {
            allowAnonymous: false,
            title: 'Acknowledge Carbucks Protect',
            animation: 'scale'
        },
        component: CarbucksProtectFlow
    },
    {
        path: '/my-garage/set-odometer',
        name: 'Set Odometer',
        meta: {
            allowAnonymous: false,
            title: 'Set Odometer',
            animation: 'scale'
        },
        component: SetOdometer
    },
    {
        path: '/my-garage/settings',
        name: 'Settings',
        meta: {
            allowAnonymous: false,
            title: 'Settings',
            animation: 'scale'
        },
        component: Settings
    },
    {
        path: '/my-garage/digital-service-log',
        name: 'My Garage - Digital Service Log',
        meta: {
            allowAnonymous: false,
            title: 'Digital Service Log',
            animation: 'scale'
        },
        component: ServiceLog
    },
    {
        path: '/my-garage/care-plan',
        name: 'My Garage - Care Plan',
        meta: {
            allowAnonymous: false,
            title: 'Care Plan',
            animation: 'scale'
        },
        component: CarePlan
    },
    {
        path: '/my-garage/care-plan/carbucks-rewards',
        name: 'My Garage - Care Plan - Carbucks Rewards',
        meta: {
            allowAnonymous: false,
            title: 'Carbucks Rewards',
            animation: 'scale'
        },
        component: () => import( './Views/CarePlan/CarbucksRewards.vue' ),
    },
    {
        path: '/my-garage/care-plan/carbucks-protect',
        name: 'My Garage - Care Plan - Carbucks Protect',
        meta: {
            allowAnonymous: false,
            title: 'Carbucks Protect',
            animation: 'scale'
        },
        component: () => import( './Views/CarePlan/CarbucksProtect.vue' ),
    },
    {
        path: '/my-garage/care-plan/item/:itemType/:conditionType?',
        name: 'My Garage - Care Plan - Item',
        meta: {
            allowAnonymous: false,
            title: 'Care Plan',
            animation: 'scale'
        },
        component: () => import( './Views/CarePlan/CarePlanItem.vue' ),
    },
    {
        path: '/my-garage/history',
        name: 'My Garage - History',
        meta: {
            allowAnonymous: false,
            title: 'History',
            animation: 'scale'
        },
        component: History
    },
    {
        path: '/my-garage/vehicle-details',
        name: 'Vehicle Details',
        meta: {
            allowAnonymous: false,
            title: 'Vehicle Details'
        },
        component: VehicleDetails
    },
    {
        path: '/my-garage/dealership',
        name: 'Home Dealership',
        meta: {
            allowAnonymous: false,
            title: 'Home Dealership'
        },
        component: () => import( './Views/Dealership/Dealership.vue' ),
    },
    {
        path: '/my-garage/dealership/change',
        name: 'Change Dealership',
        meta: {
            allowAnonymous: false,
            title: 'Change Dealership'
        },
        component: () => import( './Views/Dealership/ChangeDealership.vue' ),
    },
    {
        path: '/my-garage/health-monitor',
        name: 'More Info - Health Monitor',
        meta: {
            allowAnonymous: false,
            title: 'Health Monitor'
        },
        component: () => import( './Views/HealthMonitor/HealthMonitorStatus.vue' ),
    },
    {
        path: '/my-garage/guaranteed-future-value',
        name: 'Guaranteed Future Value',
        meta: {
            allowAnonymous: false,
            title: 'Guaranteed Future Value'
        },
        // TODO: NOT IMPLEMENTED
        component: EarlyAccess,
    },
    {
        path: '/my-garage/mechanical-protection',
        name: 'Mechanical Protection',
        meta: {
            allowAnonymous: false,
            title: 'Mechanical Protection'
        },
        // TODO: NOT IMPLEMENTED
        component: EarlyAccess,
    },    
    {
        path: '/my-garage/concierge',
        name: 'Help - Concierge',
        meta: {
            allowAnonymous: false,
            title: 'Concierge'
        },
        component: Concierge
    },
    {
        path: '/my-garage/concierge/emergency/accident',
        name: 'Help - Accident',
        meta: {
            allowAnonymous: false,
            title: 'Accident'
        },
        component: () => import( './Views/Concierge/Emergency/Accident.vue' ),
    },
    {
        path: '/my-garage/concierge/emergency/roadside-assist',
        name: 'Help - RSA',
        meta: {
            allowAnonymous: false,
            title: 'Roadside Assist'
        },
        component: () => import( './Views/Concierge/Emergency/RoadsideAssist.vue' ),
    },
    {
        path: '/my-garage/concierge/maintenance/book-service',
        name: 'Help - Book Service',
        meta: {
            allowAnonymous: false,
            title: 'Book Service'
        },
        component: () => import( './Views/Concierge/Maintenance/BookService.vue' ),
    },
    {
        path: '/my-garage/concierge/emergency/flat-battery',
        name: 'Help - Flat Battery',
        meta: {
            allowAnonymous: false,
            title: 'Flat Battery'
        },
        component: () => import( './Views/Concierge/Emergency/FlatBattery.vue' ),
    },
    {
        path: '/my-garage/concierge/emergency/flat-tyre',
        name: 'Help - Flat Tyre',
        meta: {
            allowAnonymous: false,
            title: 'Flat Tyre'
        },
        component: () => import( './Views/Concierge/Emergency/FlatTyre.vue' ),
    },
    {
        path: '/my-garage/concierge/financeandinsurance/insurance',
        name: 'Help - Insurance',
        meta: {
            allowAnonymous: false,
            title: 'Insurance'
        },
        component: () => import( './Views/Concierge/FinanceAndInsurance/Insurance.vue' ),
    },
    {
        path: '/my-garage/concierge/emergency/fuel',
        name: 'Help - Fuel',
        meta: {
            allowAnonymous: false,
            title: 'Fuel'
        },
        component: () => import( './Views/Concierge/Emergency/Fuel.vue' ),
    },
    {
        path: '/my-garage/concierge/emergency/locked-out',
        name: 'Help - Locked Out',
        meta: {
            allowAnonymous: false,
            title: 'Locked Out'
        },
        component: () => import( './Views/Concierge/Emergency/LockedOut.vue' ),
    },
    {
        path: '/my-garage/concierge/repair/scratch-and-dent',
        name: 'Help - Scratch and Dent',
        meta: {
            allowAnonymous: false,
            title: 'Scratch And Dent'
        },
        component: () => import( './Views/Concierge/Repair/ScratchAndDent.vue' ),
    },
    {
        path: '/my-garage/concierge/repair/windscreen',
        name: 'Help - Windscreen',
        meta: {
            allowAnonymous: false,
            title: 'Windscreen'
        },
        component: () => import( './Views/Concierge/Repair/Windscreen.vue' ),
    },
    {
        path: '/my-garage/condition-settings',
        name: 'Condition Settings',
        meta: {
            allowAnonymous: false,
            title: 'Condition Score'
        },
        component: ConditionSettings
    },
    {
        path: '/my-garage/more-info/battery',
        name: 'More Info - Battery',
        meta: {
            allowAnonymous: false,
            title: 'Battery'
        },
        component: () => import( './Views/MoreInfo/Battery.vue' ),
    },
    {
        path: '/my-garage/more-info/oil',
        name: 'More Info - Oil',
        meta: {
            allowAnonymous: false,
            title: 'Oil'
        },
        component: () => import( './Views/MoreInfo/Oil.vue' ),
    },
    {
        path: '/my-garage/more-info/recall',
        name: 'More Info - Recall',
        meta: {
            allowAnonymous: false,
            title: 'Recall'
        },
        component: () => import( './Views/MoreInfo/Recall.vue' ),
    },
    {
        path: '/my-garage/more-info/rego',
        name: 'More Info - Rego',
        meta: {
            allowAnonymous: false,
            title: 'Rego'
        },
        component: () => import( './Views/MoreInfo/Rego.vue' ),
    },
    {
        path: '/my-garage/more-info/service',
        name: 'More Info - Service',
        meta: {
            allowAnonymous: false,
            title: 'Service'
        },
        component: () => import( './Views/MoreInfo/Service.vue' ),
    },
    {
        path: '/my-garage/more-info/tyres',
        name: 'More Info - Tyres',
        meta: {
            allowAnonymous: false,
            title: 'Tyres'
        },
        component: () => import( './Views/MoreInfo/Tyres.vue' ),
    },
    {
        path: '/my-garage/more-info/wash',
        name: 'More Info - Wash',
        meta: {
            allowAnonymous: false,
            title: 'Wash'
        },
        component: () => import( './Views/MoreInfo/Wash.vue' ),
    },
    {
        path: '/my-garage/scan-event/:scanEventId/error-details',
        name: 'Scan Event Error Details',
        meta: {
            allowAnonymous: false,
            title: 'Scan Event Error Details'
        },
        component: () => import( './Views/HealthMonitor/HealthMonitorErrorDetails.vue' ),
    }
];
